"use client";

import { useMemo, useState, useRef, createContext, useContext } from "react";
import { XIcon, CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface User {
    username: string;
    [key: string]: any;
}

interface CustomUserFilterProps {
    users: User[];
    selectedUserIds: string[];
    onSelectedUsersChange: (userIds: string[]) => void;
    getUserDisplayName: (username: string) => string;
    children?: React.ReactNode;
}

interface UserFilterContextValue {
    users: User[];
    selectedUserIds: string[];
    onSelectedUsersChange: (userIds: string[]) => void;
    getUserDisplayName: (username: string) => string;
    inputValue: string;
    setInputValue: (value: string) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    toggleUser: (userId: string) => void;
    removeUser: (userId: string) => void;
    filteredUsers: User[];
    selectedUsers: User[];
}

const UserFilterContext = createContext<UserFilterContextValue | null>(null);

const useUserFilterContext = () => {
    const context = useContext(UserFilterContext);
    if (!context) throw new Error("Must be used within CustomUserFilter");
    return context;
};

export function CustomUserFilter({
    users,
    selectedUserIds,
    onSelectedUsersChange,
    getUserDisplayName,
    children,
}: CustomUserFilterProps) {
    const [inputValue, setInputValue] = useState("");
    const [open, setOpen] = useState(false);

    const filteredUsers = useMemo(() => {
        const filtered = !inputValue
            ? users
            : users.filter((user) =>
                  getUserDisplayName(user.username)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase()),
              );
        return [...filtered].sort((a, b) =>
            getUserDisplayName(a.username).localeCompare(
                getUserDisplayName(b.username),
            ),
        );
    }, [users, inputValue, getUserDisplayName]);

    const selectedUsers = useMemo(
        () =>
            [...users]
                .filter((user) => selectedUserIds.includes(user.username))
                .sort((a, b) =>
                    getUserDisplayName(a.username).localeCompare(
                        getUserDisplayName(b.username),
                    ),
                ),
        [users, selectedUserIds, getUserDisplayName],
    );

    const toggleUser = (userId: string) => {
        if (selectedUserIds.includes(userId)) {
            onSelectedUsersChange(
                selectedUserIds.filter((id) => id !== userId),
            );
        } else {
            onSelectedUsersChange([...selectedUserIds, userId]);
        }
    };

    const removeUser = (userId: string) => {
        onSelectedUsersChange(selectedUserIds.filter((id) => id !== userId));
    };

    return (
        <UserFilterContext.Provider
            value={{
                users,
                selectedUserIds,
                onSelectedUsersChange,
                getUserDisplayName,
                inputValue,
                setInputValue,
                open,
                setOpen,
                toggleUser,
                removeUser,
                filteredUsers,
                selectedUsers,
            }}
        >
            {children || (
                <div className="flex flex-col gap-2">
                    <CustomUserFilterInput />
                    <CustomUserFilterOutput />
                </div>
            )}
        </UserFilterContext.Provider>
    );
}

export function CustomUserFilterInput({ className }: { className?: string }) {
    const {
        inputValue,
        setInputValue,
        open,
        setOpen,
        filteredUsers,
        toggleUser,
        selectedUserIds,
        getUserDisplayName,
    } = useUserFilterContext();
    const inputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="relative">
            <input
                ref={inputRef}
                type="text"
                placeholder="Filter by users..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={(e) => {
                    if (!e.relatedTarget?.closest("[data-popover-content]")) {
                        setOpen(false);
                    }
                }}
                className={cn(
                    "w-47 h-10 px-2.5 rounded-[var(--color-radius)] border border-md-outline bg-md-surface text-md-on-surface text-sm outline-none focus:border-md-primary focus:ring-[3px] focus:ring-md-primary/20 transition-all hover:border-md-primary hover:bg-md-tertiary-container/10 placeholder:text-gray-600",
                    className,
                )}
            />
            {open && (
                <div
                    data-popover-content
                    className="absolute top-full left-0 mt-1.5 min-w-64 max-w-sm max-h-96 overflow-hidden rounded-lg border border-md-outline-variant bg-md-surface shadow-md z-50 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <div className="max-h-96 overflow-y-auto p-1">
                        {filteredUsers.length === 0 ? (
                            <div className="py-6 text-center text-sm text-md-on-surface-variant">
                                No users found
                            </div>
                        ) : (
                            filteredUsers.map((user) => {
                                const isSelected = selectedUserIds.includes(
                                    user.username,
                                );
                                return (
                                    <button
                                        key={user.username}
                                        onClick={() =>
                                            toggleUser(user.username)
                                        }
                                        className={cn(
                                            "relative flex w-full items-center gap-2 rounded-md py-1 pr-8 pl-1.5 text-sm text-left outline-none select-none cursor-pointer",
                                            "hover:bg-md-primary-container hover:text-md-on-primary-container transition-colors duration-150",
                                            isSelected && "opacity-60",
                                        )}
                                    >
                                        {getUserDisplayName(user.username)}
                                        {isSelected && (
                                            <span className="absolute right-2 flex size-4 items-center justify-center">
                                                <CheckIcon className="size-4 text-md-primary" />
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export function CustomUserFilterOutput() {
    const { selectedUsers, removeUser, getUserDisplayName } =
        useUserFilterContext();

    if (selectedUsers.length === 0) return null;

    return (
        <>
            {selectedUsers.map((user) => (
                <div
                    key={user.username}
                    className="flex h-7 items-center gap-1 rounded-sm bg-md-secondary-container text-md-on-secondary-container px-1.5 text-xs font-medium whitespace-nowrap animate-in fade-in-0 zoom-in-95 duration-150"
                >
                    {getUserDisplayName(user.username)}
                    <button
                        onClick={() => removeUser(user.username)}
                        className="opacity-50 hover:opacity-100 p-0.5 rounded hover:bg-black/10 transition-opacity duration-150"
                    >
                        <XIcon className="size-3" />
                    </button>
                </div>
            ))}
        </>
    );
}
